'use client';

import Link from 'next/link';

import type { Blog } from '@eliadmin/core/src/api/blogs/blogs-types';

import { Button } from '@eliadmin/core/src/components/ui/button';
import { SectionHeading } from '@eliadmin/core/src/components/ui/section-heading';
import { ROUTES } from '@eliadmin/core/src/constants/routes';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { PiArrowCircleRightFill } from 'react-icons/pi';
import { BlogCard } from '@eliadmin/core/src/components/blog/blog-card';
import { Carousel } from '@eliadmin/core/src/components/ui/carousel';
import { useCarouselContext } from '@eliadmin/core/src/components/ui/carousel-context';

export function Blogs({ blogs }: { blogs: Blog[] }) {
  const options = {
    align: 'start' as const,
  };

  const carouselContext = useCarouselContext();

  if (!carouselContext) {
    throw new Error('Hero must be used within a CarouselProvider');
  }

  const { selectedIndex } = carouselContext;

  return (
    <section
      className={cn(
        'custom-section',
        'bg-[#F4F7FB] py-[60px] pr-0 sm:py-20 md:pr-4 lg:py-[120px]',
        'generic-gap',
      )}
    >
      <div className={cn('custom-container', 'flex items-end justify-between gap-4')}>
        <SectionHeading
          title="<span>Užitočné rady</span><br/>z našej lekárne"
          description="Pravidelne pridávame články o zdraví, prevencii a liečbe — od našich farmaceutov."
          className={{ container: 'w-full max-w-[588px] pr-4 md:pr-0' }}
          align="left"
        />
        <Button asChild className="hidden whitespace-nowrap sm:flex" variant={'outline'}>
          <Link prefetch={false} href={ROUTES.BLOG()}>
            Všetky články <PiArrowCircleRightFill size={20} className="text-secondary" />
          </Link>
        </Button>
      </div>
      <div className={cn('custom-container', 'hidden grid-cols-3 gap-4 md:grid lg:gap-6')}>
        {blogs.map(blog => (
          <BlogCard blog={blog} key={blog.name} />
        ))}
      </div>
      <div className="flex w-full flex-col items-center justify-center gap-[30px] md:hidden">
        <Carousel
          options={options}
          className={{
            wrapper: 'w-full',
            main: 'w-full',
            container: 'w-full gap-3',
            slide: 'w-full max-w-[340px] overflow-hidden rounded-[24px] sm:max-w-[360px]',
          }}
          slides={blogs.map(blog => (
            <BlogCard blog={blog} key={blog.name} />
          ))}
        />
        <div className="flex w-full items-center justify-center gap-2 pr-4">
          {blogs.map((_, index) => (
            <div
              key={index}
              className={cn(
                'h-2 w-2 rounded-full',
                index === selectedIndex ? 'bg-primary' : 'bg-border',
              )}
            />
          ))}
        </div>
        <div className="w-full pr-4">
          <Button asChild variant={'outline'} className="w-full">
            <Link href={ROUTES.BLOG()} prefetch={false}>
              Všetky články
              <PiArrowCircleRightFill size={20} className="text-secondary" />
            </Link>
          </Button>
        </div>
      </div>
    </section>
  );
}
